import classNames from 'classnames';

import { PricelistProductTranslation } from '../../../localization/ModuleTranslation';
import type { PricelistProductDetailsData } from '../../../model';
import ProductImage from '../../../shared/ProductImage/ProductImage';
import { ProductPricelist, ProductPricelistSkeleton } from '../../../shared/ProductPricelist/ProductPricelist';
import { Menu, MenuButton, MenuContent, MenuItem } from '../../../ui/Menu/Menu';
import Skeleton from '../../../ui/Skeleton/Skeleton';
import SkeletonText from '../../../ui/SkeletonText/SkeletonText';
import ExpandableProductAccessories from './ExpandableProductAccessories/ExpandableProductAccessories';
import ExpandableProductAccessoriesState from './ExpandableProductAccessories/ExpandableProductAccessoriesState';
import ExpandableProductAdditionalInfo from './ExpandableProductAdditionalInfo/ExpandableProductAdditionalInfo';
import css from './PricelistProduct.css';

interface PricelistProductAction {
  label: string;
  action: () => void;
}

interface PricelistProductProps {
  accessories: ExpandableProductAccessoriesState;
  product: PricelistProductDetailsData;
  translation: PricelistProductTranslation;
  actions?: PricelistProductAction[];
  className?: string;
}

export const PricelistProduct = ({ accessories, actions, className, product, translation }: PricelistProductProps) => {
  return (
    <article className={className}>
      <header className={css.header}>
        <ProductImage
          imageUrl={product.image?.url}
          productName={product.name}
          productDescription={product.shortDescription}
          translation={translation.productImage}
          size="medium"
        />
        <div className={css.headerText}>
          <h2 className={css.heading}>{product.name}</h2>
          {product.types.any() && (
            <p className={css.types}>
              {product.types.map((type) => (
                <span key={type}>{type}</span>
              ))}
            </p>
          )}
          {product.shortDescription && <p className={css.description}>{product.shortDescription}</p>}
        </div>
        {actions?.any() && (
          <Menu>
            <MenuButton
              className={css.actions}
              aria-label={translation.actionsLabelFormat.interpolate([['name', product.name]])}
            />
            <MenuContent>
              {actions.map(({ action, label }) => (
                <MenuItem key={label} onAction={action}>
                  {label}
                </MenuItem>
              ))}
            </MenuContent>
          </Menu>
        )}
      </header>
      <ProductPricelist
        displayOptions={product.displayOptions}
        basePrice={product.basePrice}
        netBasePrice={product.netBasePrice}
        priceGroups={product.priceGroups}
        translation={translation.pricelist}
      />
      <ExpandableProductAccessories
        className={css.accessories}
        state={accessories}
        translation={translation.accessories}
      />
      <ExpandableProductAdditionalInfo
        className={css.additionalInfo}
        additionalInfo={product.additionalInfo}
        heading={translation.additionalInfoHeading}
      />
    </article>
  );
};

interface PricelistProductSkeletonProps {
  translation: PricelistProductTranslation;
  className?: string;
}

export const PricelistProductSkeleton = ({ className, translation }: PricelistProductSkeletonProps) => {
  return (
    <div className={classNames(css.skeleton, className)}>
      <div className={css.header}>
        <Skeleton className={css.image} />
        <SkeletonText className={css.headerText} lineCount={3} />
      </div>
      <ProductPricelistSkeleton translation={translation.pricelist} />
      <Skeleton className={css.accessories} />
    </div>
  );
};
